import { useState, useEffect, useRef } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import { SEO, OrganizationJsonLd, WebsiteJsonLd } from '@/components/SEO';
import { useAuth } from '@/context/AuthContext';
import { useTheme } from '@/context/ThemeContext';

import axios from 'axios';
import { 
  Car, 
  MapPin, 
  Users, 
  Star, 
  Shield, 
  Zap,
  ChevronRight,
  Menu,
  X,
  ArrowRight,
  CheckCircle,
  Phone,
  Mail,
  BadgeCheck,
  Sparkles,
  Calendar,
  Navigation,
  PartyPopper,
  LogOut,
  Download,
  Instagram,
  ChevronDown,
  Compass
} from 'lucide-react';
import HeroVideo from '@/components/HeroVideo';
import SmartInstallModal from '@/components/SmartInstallModal';
import ShowcasePage from '@/pages/ShowcasePage';
import MobileMenuOverlay from '@/components/layout/MobileMenuOverlay';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const LandingPage = () => {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const { mode } = useTheme();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [crisisZoneVisible, setCrisisZoneVisible] = useState(false);

  useEffect(() => {
    axios.get(`${API}/admin/crisis-zone-visibility`)
      .then(res => setCrisisZoneVisible(res.data.crisis_zone_visible))
      .catch(() => {});
  }, []);

  // Capture invite ref code from URL (?ref=CODE) on first landing and
  // persist it so the user can browse → FAQ → Showcase → Register, and
  // the referral still attributes correctly. Cleared by RegisterPage on
  // successful signup.
  useEffect(() => {
    try {
      const params = new URLSearchParams(window.location.search);
      const ref = params.get('ref');
      if (ref && ref.trim()) {
        localStorage.setItem('rollm8s_referral_code', ref.trim());
      }
    } catch {}
  }, []);
  const [desktopMenuOpen, setDesktopMenuOpen] = useState(false);
  const desktopMenuRef = useRef(null);
  const [popularRoutes, setPopularRoutes] = useState([]);
  const [platformStats, setPlatformStats] = useState(null);
  const [openRequests, setOpenRequests] = useState([]);
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [showInstallModal, setShowInstallModal] = useState(false);
  const promptRef = useRef(null);
  const [heroVideoUrl, setHeroVideoUrl] = useState(null);
  const [heroVideoFailed, setHeroVideoFailed] = useState(false);
  const [heroVideoTransform, setHeroVideoTransform] = useState(null);
  const heroVideoRef = useRef(null);
  const [showSplash, setShowSplash] = useState(() => !sessionStorage.getItem('splash-shown'));
  const [splashPhase, setSplashPhase] = useState('spin'); // 'spin' | 'waiting' | 'exploding'
  const [showShowcase, setShowShowcase] = useState(false);

  // Splash screen — after spin, wait for tap (1.5s delay + 3.6s spin + buffer)
  useEffect(() => {
    if (showSplash && splashPhase === 'spin') {
      const timer = setTimeout(() => {
        setSplashPhase('waiting');
      }, 5400);
      return () => clearTimeout(timer);
    }
  }, [showSplash, splashPhase]);

  const handleEnterSite = () => {
    setSplashPhase('exploding');
    // Trigger music autoplay from user interaction
    window.dispatchEvent(new CustomEvent('autoplayMusic'));
    // Wait for explosion to finish, then show site
    setTimeout(() => {
      setShowSplash(false);
      sessionStorage.setItem('splash-shown', 'true');
      // Show showcase overlay after landing page loads
      if (!sessionStorage.getItem('showcase-shown')) {
        setTimeout(() => setShowShowcase(true), 600);
      }
    }, 2200);
  };

  // Mute video when music player starts, unmute when it stops
  useEffect(() => {
    const handleMusicState = (e) => {
      if (heroVideoRef.current) {
        if (e.detail.playing) {
          heroVideoRef.current.mute();
        } else {
          heroVideoRef.current.unmute();
        }
      }
    };
    window.addEventListener('musicPlayerStateChange', handleMusicState);
    return () => window.removeEventListener('musicPlayerStateChange', handleMusicState);
  }, []);

  const isStandalone = window.matchMedia('(display-mode: standalone)').matches || window.navigator.standalone;

  // Redirect logged-in users to dashboard (skip marketing landing)
  useEffect(() => {
    if (user && !showSplash) {
      navigate('/dashboard', { replace: true });
    }
  }, [user, showSplash, navigate]);

  const handleReplayTour = () => {
    setDesktopMenuOpen(false);
    setMobileMenuOpen(false);
    localStorage.setItem('rollm8s_replay_tour', 'true');
    navigate('/dashboard');
  };

  useEffect(() => {
    const handler = (e) => { e.preventDefault(); promptRef.current = e; setDeferredPrompt(e); };
    window.addEventListener('beforeinstallprompt', handler);
    return () => window.removeEventListener('beforeinstallprompt', handler);
  }, []);

  // Close desktop dropdown on click outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (desktopMenuRef.current && !desktopMenuRef.current.contains(e.target)) {
        setDesktopMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleInstallApp = () => {
    setShowInstallModal(true);
  };

  useEffect(() => {
    fetchPopularRoutes();
    fetchPlatformStats();
    fetchOpenRequests();
    checkHeroVideo();
  }, []);

  const checkHeroVideo = async () => {
    try {
      const response = await axios.get(`${API}/site-settings`);
      if (response.data?.hero_video_url) {
        let url = response.data.hero_video_url;
        if (url.startsWith('/')) {
          url = `${BACKEND_URL}${url}`;
        }
        setHeroVideoUrl(url);
        if (response.data.hero_video_zoom || response.data.hero_video_position_x !== undefined) {
          setHeroVideoTransform({
            zoom: response.data.hero_video_zoom || 1,
            positionX: response.data.hero_video_position_x ?? 50,
            positionY: response.data.hero_video_position_y ?? 50,
          });
        }
      }
    } catch (error) {
      console.log('No hero video configured');
    }
  };

  const fetchPopularRoutes = async () => {
    try {
      const response = await axios.get(`${API}/popular-routes`);
      setPopularRoutes(response.data);
    } catch (error) {
      console.error('Error fetching routes:', error);
    }
  };

  const fetchPlatformStats = async () => {
    try {
      const response = await axios.get(`${API}/platform-stats`);
      setPlatformStats(response.data);
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  };

  const fetchOpenRequests = async () => {
    try {
      const token = localStorage.getItem('rollm8s_token') || localStorage.getItem('token');
      if (token) {
        const response = await axios.get(`${API}/open-ride-requests/grouped`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setOpenRequests(response.data || []);
      }
    } catch (error) {
      console.log('Open requests not available or not logged in');
    }
  };

  const features = [
    {
      icon: <Car className="w-8 h-8 text-primary" />,
      title: "Event-Centric Rides",
              description: "Find drivers heading to the same skate, board or wheel events. Real-time sat-nav navigation for drivers and live tracking for passengers."
    },
    {
      icon: <Navigation className="w-8 h-8 text-secondary" />,
      title: "Blackout Zone & Offline Maps",
      description: "72,000+ UK resources mapped. Download road networks for fully offline turn-by-turn navigation — no signal needed."
    },
    {
      icon: <Users className="w-8 h-8 text-accent" />,
      title: "Community First",
      description: "Community Listings noticeboard, Safe Space peer support, DJ & Organiser Corners, Unfiltered chat, and Floor Tax fun."
    },
    {
      icon: <Compass className="w-8 h-8 text-primary" />,
      title: "World Trips",
      description: "Discover international skate, wheel & board events worldwide — roller, inline, skateboard, BMX, scooter, snow sports & more. See who's going, mark your attendance, and plan your next trip."
    }
  ];

  const stats = platformStats ? [
    { value: platformStats.total_users.toString(), label: "Users Joined" },
    { value: platformStats.total_events.toString(), label: "Events Listed" },
  ] : [
    { value: "0", label: "Users Joined" },
    { value: "0", label: "Events Listed" },
  ];

  const trustFeatures = [
    { icon: <Phone className="w-5 h-5" />, label: "Phone Verified" },
    { icon: <Car className="w-5 h-5" />, label: "DVLA Verified" },
    { icon: <Star className="w-5 h-5" />, label: "Ratings & Reviews" },
    { icon: <Users className="w-5 h-5" />, label: "Community Member Check" }
  ];

  if (showSplash) {
    return (
      <div className={`fixed inset-0 z-[200] bg-black flex flex-col items-center justify-center overflow-hidden ${splashPhase === 'exploding' ? 'splash-container-exit' : ''}`}>
        {/* Animated background rings */}
        <div className="absolute inset-0 flex items-center justify-center">
          <div className="w-[600px] h-[600px] rounded-full border border-primary/10 animate-[ping_3s_ease-out_infinite]" />
          <div className="absolute w-[400px] h-[400px] rounded-full border border-primary/5 animate-[ping_3s_ease-out_0.5s_infinite]" />
        </div>
        
        {/* Explosion particles — only when exploding */}
        {splashPhase === 'exploding' && (
          <div className="splash-particles absolute inset-0 pointer-events-none">
            {[...Array(16)].map((_, i) => (
              <div 
                key={i} 
                className="splash-particle absolute left-1/2 top-1/2"
                style={{
                  '--angle': `${(i / 16) * 360}deg`,
                  '--delay': `${0.3 + (i % 4) * 0.05}s`,
                  '--distance': `${150 + (i % 3) * 80}px`,
                  '--size': `${6 + (i % 3) * 4}px`,
                }}
              />
            ))}
          </div>
        )}
        
        {/* Logo container */}
        <div className="relative z-10 flex flex-col items-center gap-6">
          <div className={`splash-logo-container ${splashPhase === 'exploding' ? 'splash-exploding' : ''}`}>
            <img fetchPriority="high"
              src="/rollm8s-logo-dark.png"
              alt="RollM8s" 
              className="w-32 h-32 object-contain splash-logo"
            />
          </div>
          <div className={`splash-text flex flex-col items-center ${splashPhase === 'exploding' ? 'splash-text-exit' : ''}`}>
            <span className="font-logo text-5xl tracking-wide">
              <span className="text-white">ROLL</span>
              <span className="gradient-text">M8S</span>
            </span>
            <p className="text-[0.6rem] uppercase font-bold whitespace-nowrap mt-1 w-full" style={{ textAlign: 'justify', textAlignLast: 'justify' }}>
              <span className="text-white">FOR SKATE, WHE</span>
              <span className="text-primary">ELS </span>
              <span className="text-secondary">& BOARDS</span>
            </p>
          </div>
        </div>
          
        {/* Tap to Enter — positioned absolutely so it doesn't shift the logo */}
        {splashPhase === 'waiting' && (
          <button 
            onClick={handleEnterSite}
            className="splash-enter absolute z-10 px-8 py-3 rounded-full border border-primary/50 text-primary text-sm tracking-widest uppercase hover:bg-primary/10 transition-all animate-pulse"
            style={{ top: '72%' }}
            data-testid="splash-enter-btn"
          >
            Tap to Enter
          </button>
        )}

        <style>{`
          .splash-container-exit {
            animation: splashContainerFade 2.2s ease-in forwards;
          }
          @keyframes splashContainerFade {
            0% { background-color: black; }
            70% { background-color: black; }
            100% { background-color: black; opacity: 0; }
          }
          .splash-logo-container {
            animation: splashSpin 3.6s cubic-bezier(0.34, 1.56, 0.64, 1) 1.5s both;
          }
          .splash-logo {
            filter: drop-shadow(0 0 30px hsl(var(--primary) / 0.5));
          }
          .splash-exploding {
            animation: splashExplode 2s ease-in forwards !important;
          }
          .splash-text {
            opacity: 0;
            transform: translateY(20px);
            animation: splashFadeUp 0.6s ease-out 2.7s forwards;
          }
          .splash-text-exit {
            animation: splashFadeUp 0.6s ease-out 1s forwards, splashFadeOut 0.4s ease-in forwards !important;
          }
          .splash-tagline {
            opacity: 0;
            transform: translateY(10px);
            animation: splashFadeUp 0.6s ease-out 3.2s forwards;
          }
          .splash-enter {
            animation: splashFadeUp 0.5s ease-out forwards;
          }
          .splash-particle {
            width: var(--size);
            height: var(--size);
            border-radius: 50%;
            background: hsl(var(--primary));
            opacity: 0;
            transform: translate(-50%, -50%) rotate(var(--angle)) translateY(0);
            animation: particleBurst 2s cubic-bezier(0.25, 0.46, 0.45, 0.94) var(--delay) forwards;
          }
          .splash-particle:nth-child(odd) {
            background: hsl(var(--secondary, var(--primary)));
          }
          .splash-particle:nth-child(3n) {
            background: white;
          }
          @keyframes splashSpin {
            0% { transform: translateX(calc(-50vw - 100px)) rotate(-720deg); opacity: 1; }
            65% { transform: translateX(5%) rotate(15deg); opacity: 1; }
            80% { transform: translateX(-2%) rotate(-5deg); }
            100% { transform: translateX(0) rotate(0deg); }
          }
          @keyframes splashExplode {
            0% { transform: scale(1); opacity: 1; filter: brightness(1); }
            30% { transform: scale(1.3); filter: brightness(1.5); }
            100% { transform: scale(3); opacity: 0; filter: brightness(2); }
          }
          @keyframes splashFadeUp {
            to { opacity: 1; transform: translateY(0); }
          }
          @keyframes splashFadeOut {
            to { opacity: 0; transform: scale(0.8); }
          }
          @keyframes particleBurst {
            0% { opacity: 1; transform: translate(-50%, -50%) rotate(var(--angle)) translateY(0) scale(1); }
            100% { opacity: 0; transform: translate(-50%, -50%) rotate(var(--angle)) translateY(calc(-1 * var(--distance))) scale(0); }
          }
        `}</style>
      </div>
    );
  }

  // Don't render landing page if logged-in user is about to redirect to dashboard
  if (user && !showSplash) {
    return <div className="min-h-screen bg-background" />;
  }

  return (
    <div className="min-h-screen relative overflow-hidden">
      <SEO
        title="UK Skate, Wheels & Boards Community Platform"
        description="The UK Skate, Wheels & Boards community hub — built first for roller skaters, now home to inline, skateboard, longboard, BMX, scooter and snow sports communities. Find events, buy tickets, share rides, buy & sell gear, access 72,000+ offline crisis resources, plan world trips, and connect with your crew nationwide."
        path="/"
      />
      <OrganizationJsonLd />
      <WebsiteJsonLd />
      {/* Swing Tag Animation */}
      <style>{`
        .swing-tag {
          transform-origin: top center;
          animation: swingTag 3s ease-in-out infinite;
        }
        @keyframes swingTag {
          0%, 100% { transform: rotate(-10deg); }
          50% { transform: rotate(10deg); }
        }
      `}</style>

      {/* Noise overlay */}
      <div className="noise-overlay absolute inset-0 pointer-events-none" />

      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 glass border-b border-border/50 pt-[calc(env(safe-area-inset-top,0px)+0.75rem)] md:pt-0">
        <div className="max-w-7xl mx-auto px-2 md:px-8 relative">
          {/* Swinging Luggage Tag — mobile only */}
          <div className="absolute right-2 top-[75px] z-10 md:hidden" data-testid="event-ticket-tag-mobile">
            <div className="flex justify-center">
              <div className="w-3 h-3 rounded-full bg-gray-300 border-2 border-gray-500 shadow-[0_0_6px_rgba(0,0,0,0.5)]" />
            </div>
            <div className="swing-tag" style={{ transformOrigin: 'top center', marginTop: '-4px' }}>
              <svg className="mx-auto" width="32" height="22" viewBox="0 0 32 22">
                <path d="M10 0 Q10 8 16 10 Q22 8 22 0" stroke="#999" strokeWidth="1.3" fill="none" />
                <line x1="16" y1="10" x2="16" y2="22" stroke="#999" strokeWidth="1.3" />
              </svg>
              <div className="relative" style={{
                width: '105px',
                marginTop: '-2px',
                background: 'linear-gradient(155deg, #FFD700, #FFC107, #FFB300)',
                clipPath: 'polygon(0% 14%, 50% 0%, 100% 14%, 100% 100%, 0% 100%)',
                borderRadius: '0 0 12px 12px',
                boxShadow: '2px 4px 14px rgba(0,0,0,0.45)',
                padding: '20px 8px 14px',
              }}>
                <div className="absolute left-1/2 -translate-x-1/2" style={{ top: '7px' }}>
                  <div className="w-3 h-3 rounded-full border-2 border-yellow-700/50 bg-yellow-900/30" />
                </div>
                <div className="text-center mt-1">
                  <p className="font-bold text-gray-900 text-[10px] uppercase leading-tight tracking-wide">
                    Sell Event Tickets & Sk8 Merch Here
                  </p>
                  <div className="mt-1.5 pt-1 border-t border-yellow-800/25">
                    <p className="text-yellow-900/60 text-[8px] font-semibold uppercase tracking-wider">For Us By Us</p>
                    <p className="font-extrabold text-gray-900 text-[9px] leading-tight mt-0.5 uppercase">Community<br/>First</p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="flex flex-wrap items-center justify-between py-3 md:py-2">
            {/* Logo */}
            <Link to="/" className="flex items-center gap-1 md:-ml-10">
              <img fetchPriority="high"
                src={mode === 'dark' ? '/rollm8s-logo-dark.png' : '/rollm8s-logo-light.png'} 
                alt="RollM8s Logo" 
                className="w-[8rem] h-[8rem] md:w-[10rem] md:h-[10rem] object-contain flex-shrink-0"
              />
              <div className="flex flex-col">
                <span className="font-logo text-[3rem] md:text-[3.5rem] tracking-wide leading-tight">
                  <span className="text-foreground">ROLL</span>
                  <span className="gradient-text">M8S</span>
                </span>
                <span className="text-[10px] font-bold uppercase leading-none block w-full" style={{ textAlign: 'justify', textAlignLast: 'justify' }}>
                  <span className="text-foreground">FOR SKATE, WHE</span>
                  <span className="text-primary">ELS </span>
                  <span className="text-secondary">& BOARDS</span>
                </span>
              </div>
            </Link>

            {/* Desktop Nav */}
            <div className="hidden md:flex items-center gap-4 text-sm">
              <Link to="/events" className="text-muted-foreground hover:text-foreground transition-colors">
                Events
              </Link>
              <Link to="/rollflix" className="text-muted-foreground hover:text-foreground transition-colors">
                Community
              </Link>
              <Link to="/safe-space" className="text-muted-foreground hover:text-foreground transition-colors">
                Safe Space
              </Link>
              <Link to="/sk8-shop" className="text-muted-foreground hover:text-foreground transition-colors">
                Community Listings
              </Link>
              <Link to="/ranting-spot" className="text-muted-foreground hover:text-foreground transition-colors">
                Unfiltered
              </Link>
              <Link to="/floor-tax" className="text-muted-foreground hover:text-foreground transition-colors">
                Floor Tax
              </Link>
              <Link to="/calendar" className="text-muted-foreground hover:text-foreground transition-colors">
                Calendar
              </Link>
              {crisisZoneVisible && (
                <Link to="/blackout" className="text-muted-foreground hover:text-foreground transition-colors" data-testid="landing-desktop-crisis-zone">
                  Blackout Zone
                </Link>
              )}
              {user ? (
                <>
                  <Link to="/dashboard" className="text-muted-foreground hover:text-foreground transition-colors">
                    Dashboard
                  </Link>
                  {/* Dropdown Menu */}
                  <div className="relative" ref={desktopMenuRef}>
                    <button
                      onClick={() => setDesktopMenuOpen(!desktopMenuOpen)}
                      className="flex items-center gap-1 text-muted-foreground hover:text-foreground transition-colors"
                      data-testid="desktop-dropdown-btn"
                    >
                      <Menu className="w-4 h-4" />
                      Menu
                      <ChevronDown className={`w-3 h-3 transition-transform ${desktopMenuOpen ? 'rotate-180' : ''}`} />
                    </button>
                    {desktopMenuOpen && (
                      <div className="absolute right-0 top-full mt-2 w-52 rounded-xl glass border border-border/50 shadow-xl py-2 z-50 animate-fade-in">
                        <Link to="/my-profile" className="block px-4 py-2 text-sm text-foreground hover:bg-primary/10 transition-colors" onClick={() => setDesktopMenuOpen(false)}>
                          Profile Settings
                        </Link>
                        <Link to="/fuel-estimator" className="block px-4 py-2 text-sm text-foreground hover:bg-primary/10 transition-colors" onClick={() => setDesktopMenuOpen(false)}>
                          Fuel Calculator
                        </Link>
                        <Link to="/safety" className="block px-4 py-2 text-sm text-foreground hover:bg-primary/10 transition-colors" onClick={() => setDesktopMenuOpen(false)}>
                          Safety
                        </Link>
                        <Link to="/support" className="block px-4 py-2 text-sm text-foreground hover:bg-primary/10 transition-colors" onClick={() => setDesktopMenuOpen(false)}>
                          Support
                        </Link>
                        <Link to="/trip-history" className="block px-4 py-2 text-sm text-foreground hover:bg-primary/10 transition-colors" onClick={() => setDesktopMenuOpen(false)}>
                          Trip History
                        </Link>
                        <Link to="/my-tickets" className="block px-4 py-2 text-sm text-foreground hover:bg-primary/10 transition-colors" onClick={() => setDesktopMenuOpen(false)}>
                          Ticket Wallet
                        </Link>
                        {!isStandalone && (
                          <button onClick={() => { handleInstallApp(); setDesktopMenuOpen(false); }} className="w-full text-left px-4 py-2 text-sm text-primary hover:bg-primary/10 transition-colors flex items-center gap-2" data-testid="landing-desktop-install-btn">
                            <Download className="w-4 h-4" />
                            Download App
                          </button>
                        )}
                        {(user.is_admin || user.role === 'admin') && (
                          <>
                            <div className="border-t border-border/30 my-1" />
                            <Link to="/admin" className="block px-4 py-2 text-sm text-yellow-500 hover:bg-yellow-500/10 transition-colors font-medium" onClick={() => setDesktopMenuOpen(false)}>
                              Admin Panel
                            </Link>
                            <Link to="/site-settings" className="block px-4 py-2 text-sm text-yellow-500 hover:bg-yellow-500/10 transition-colors font-medium" onClick={() => setDesktopMenuOpen(false)}>
                              Site Settings
                            </Link>
                          </>
                        )}
                        <div className="border-t border-border/30 my-1" />
                        {/* Tour hidden */}
                        <button
                          onClick={async () => { await logout(); setDesktopMenuOpen(false); navigate('/'); }}
                          className="w-full text-left px-4 py-2 text-sm text-destructive hover:bg-destructive/10 transition-colors flex items-center gap-2"
                          data-testid="desktop-logout-btn"
                        >
                          <LogOut className="w-4 h-4" />
                          Logout
                        </button>
                      </div>
                    )}
                  </div>
                  {/* Luggage Tag for logged-in users */}
                  <div className="relative" data-testid="event-ticket-tag-wrapper-loggedin">
                    <div className="absolute left-1/2 -translate-x-1/2 top-full mt-1 z-50" data-testid="event-ticket-tag-loggedin">
                      <div className="flex justify-center">
                        <div className="w-5 h-5 rounded-full bg-gray-300 border-2 border-gray-500 shadow-[0_0_8px_rgba(0,0,0,0.5)]" />
                      </div>
                      <div className="swing-tag" style={{ transformOrigin: 'top center', marginTop: '-5px' }}>
                        <svg className="mx-auto" width="48" height="34" viewBox="0 0 48 34">
                          <path d="M14 0 Q14 12 24 15 Q34 12 34 0" stroke="#999" strokeWidth="1.5" fill="none" />
                          <line x1="24" y1="15" x2="24" y2="34" stroke="#999" strokeWidth="1.5" />
                        </svg>
                        <div className="relative" style={{
                          width: '175px',
                          marginTop: '-2px',
                          background: 'linear-gradient(155deg, #FFD700, #FFC107, #FFB300)',
                          clipPath: 'polygon(0% 12%, 50% 0%, 100% 12%, 100% 100%, 0% 100%)',
                          borderRadius: '0 0 14px 14px',
                          boxShadow: '2px 6px 18px rgba(0,0,0,0.45)',
                          padding: '28px 12px 18px',
                        }}>
                          <div className="absolute left-1/2 -translate-x-1/2" style={{ top: '9px' }}>
                            <div className="w-5 h-5 rounded-full border-2 border-yellow-700/50 bg-yellow-900/30" />
                          </div>
                          <div className="text-center mt-1">
                            <p className="font-bold text-gray-900 text-[13px] uppercase leading-tight tracking-wide">
                              Sell Event Tickets & Sk8 Merch Here
                            </p>
                            <div className="mt-2 pt-1.5 border-t border-yellow-800/25">
                              <p className="text-yellow-900/60 text-[10px] font-semibold uppercase tracking-wider">For Us By Us</p>
                              <p className="font-extrabold text-gray-900 text-[12px] leading-tight mt-0.5 uppercase">Community<br/>First</p>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </>
              ) : (
                <>
                  {/* Dropdown Menu for guests */}
                  <div className="relative" ref={desktopMenuRef}>
                    <button
                      onClick={() => setDesktopMenuOpen(!desktopMenuOpen)}
                      className="flex items-center gap-1 text-muted-foreground hover:text-foreground transition-colors"
                      data-testid="desktop-dropdown-btn-guest"
                    >
                      <Menu className="w-4 h-4" />
                      More
                      <ChevronDown className={`w-3 h-3 transition-transform ${desktopMenuOpen ? 'rotate-180' : ''}`} />
                    </button>
                    {desktopMenuOpen && (
                      <div className="absolute right-0 top-full mt-2 w-48 rounded-xl glass border border-border/50 shadow-xl py-2 z-50 animate-fade-in">
                        <Link to="/fuel-estimator" className="block px-4 py-2 text-sm text-foreground hover:bg-primary/10 transition-colors" onClick={() => setDesktopMenuOpen(false)}>
                          Fuel Calculator
                        </Link>
                        <Link to="/safety" className="block px-4 py-2 text-sm text-foreground hover:bg-primary/10 transition-colors" onClick={() => setDesktopMenuOpen(false)}>
                          Safety
                        </Link>
                        {!isStandalone && (
                          <button onClick={() => { handleInstallApp(); setDesktopMenuOpen(false); }} className="w-full text-left px-4 py-2 text-sm text-primary hover:bg-primary/10 transition-colors flex items-center gap-2" data-testid="landing-desktop-install-btn-guest">
                            <Download className="w-4 h-4" />
                            Download App
                          </button>
                        )}
                      </div>
                    )}
                  </div>
                  <div className="relative" data-testid="event-ticket-tag-wrapper">
                    <Button onClick={() => navigate('/register')} className="rounded-full bg-primary hover:bg-primary/90" data-testid="get-started-btn">
                      Get Started <span className="inline-block animate-bounce-x ml-1">&rarr;</span>
                    </Button>
                    {/* Swinging Luggage Tag — hangs below Get Started */}
                    <div className="absolute left-1/2 -translate-x-1/2 top-full mt-1 z-50" data-testid="event-ticket-tag">
                      <div className="flex justify-center">
                        <div className="w-5 h-5 rounded-full bg-gray-300 border-2 border-gray-500 shadow-[0_0_8px_rgba(0,0,0,0.5)]" />
                      </div>
                      <div className="swing-tag" style={{ transformOrigin: 'top center', marginTop: '-5px' }}>
                        <svg className="mx-auto" width="48" height="34" viewBox="0 0 48 34">
                          <path d="M14 0 Q14 12 24 15 Q34 12 34 0" stroke="#999" strokeWidth="1.5" fill="none" />
                          <line x1="24" y1="15" x2="24" y2="34" stroke="#999" strokeWidth="1.5" />
                        </svg>
                        <div className="relative" style={{
                          width: '175px',
                          marginTop: '-2px',
                          background: 'linear-gradient(155deg, #FFD700, #FFC107, #FFB300)',
                          clipPath: 'polygon(0% 12%, 50% 0%, 100% 12%, 100% 100%, 0% 100%)',
                          borderRadius: '0 0 14px 14px',
                          boxShadow: '2px 6px 18px rgba(0,0,0,0.45)',
                          padding: '28px 12px 18px',
                        }}>
                          <div className="absolute left-1/2 -translate-x-1/2" style={{ top: '9px' }}>
                            <div className="w-5 h-5 rounded-full border-2 border-yellow-700/50 bg-yellow-900/30" />
                          </div>
                          <div className="text-center mt-1">
                            <p className="font-bold text-gray-900 text-[13px] uppercase leading-tight tracking-wide">
                              Sell Event Tickets & Sk8 Merch Here
                            </p>
                            <div className="mt-2 pt-1.5 border-t border-yellow-800/25">
                              <p className="text-yellow-900/60 text-[10px] font-semibold uppercase tracking-wider">For Us By Us</p>
                              <p className="font-extrabold text-gray-900 text-[12px] leading-tight mt-0.5 uppercase">Community<br/>First</p>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </>
              )}
            </div>

            {/* Mobile menu button — icon-only, native app feel */}
            <div className="md:hidden w-full flex justify-start pl-2 -mt-2 -mb-2">
              <button
                type="button"
                className="w-10 h-10 inline-flex items-center justify-center rounded-full text-foreground/80 hover:text-foreground hover:bg-foreground/10 active:bg-foreground/15 transition"
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                aria-label={mobileMenuOpen ? 'Close menu' : 'Open menu'}
                aria-expanded={mobileMenuOpen}
                title="Menu"
                data-testid="mobile-menu-btn"
              >
                {mobileMenuOpen ? <X className="w-5 h-5" strokeWidth={2.25} /> : <Menu className="w-5 h-5" strokeWidth={2.25} />}
              </button>
            </div>
          </div>
        </div>

      </nav>

      {/* Mobile menu — unified iOS-style sheet */}
      <MobileMenuOverlay
        open={mobileMenuOpen}
        onClose={() => setMobileMenuOpen(false)}
        user={user}
        crisisZoneVisible={crisisZoneVisible}
        isStandalone={isStandalone}
        onInstall={handleInstallApp}
        onLogout={async () => { await logout(); navigate('/'); }}
      />

      {/* Hero Section */}
      <section className="relative pt-56 md:pt-60 pb-4 md:pb-10 px-4 md:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 items-start">
            {/* Left content */}
            <div className="space-y-6 animate-slide-up mt-2 md:mt-4">
              <h1 className="font-heading text-5xl md:text-7xl leading-tight uppercase">
                Roll Together,
                <br />
                <span className="gradient-text">Save Together</span>
              </h1>
              
              <p className="text-xl text-muted-foreground max-w-lg">
                The UK Skate, Wheels & Boards community hub — built first for roller skaters,
                now home to inline, skateboard, longboard, BMX, scooter and snow sports crews too.
                Find events, purchase tickets, share rides, split petrol costs, buy & sell gear,
                explore 72,000+ crisis resources on an offline map, discover world trips,
                and connect with your crew — all in one place.
              </p>

              {user ? (
                <div className="flex flex-col sm:flex-row gap-4 md:mt-16">
                  <Button 
                    onClick={() => navigate('/dashboard')} 
                    className="rounded-full px-8 py-6 text-lg font-bold bg-primary hover:bg-primary/90 neon-glow animate-pulse shadow-lg shadow-primary/50 md:translate-y-[38px]"
                    data-testid="hero-cta-btn"
                  >
                    Go to My Dashboard
                    <ChevronRight className="w-5 h-5 ml-2" />
                  </Button>
                </div>
              ) : (
                <div className="flex flex-col sm:flex-row gap-4 md:mt-16">
                  <Button 
                    onClick={() => navigate('/register')} 
                    className="rounded-full px-8 py-6 text-lg font-bold bg-primary hover:bg-primary/90 neon-glow md:translate-y-[38px]"
                    data-testid="hero-cta-btn"
                  >
                    Register / Login <span className="inline-block animate-bounce-x ml-1">&rarr;</span>
                  </Button>
                </div>
              )}
            </div>

            {/* Right content - Hero video or image */}
            <div className="relative">
              {heroVideoUrl && !heroVideoFailed ? (
                <HeroVideo ref={heroVideoRef} videoUrl={heroVideoUrl} transform={heroVideoTransform} onError={() => setHeroVideoFailed(true)} />
              ) : (
                <div>
                  <div className="absolute inset-0 bg-gradient-to-br from-primary/20 to-secondary/20 rounded-3xl blur-3xl" />
                  <img loading="lazy"
                    src="https://images.unsplash.com/photo-1685535105567-e239750f5bb2?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NzR8MHwxfHNlYXJjaHwxfHxyZXRybyUyMHJvbGxlciUyMHNrYXRlcyUyMGNvbG9yZnVsJTIwYXJ0aXN0aWN8ZW58MHx8fHwxNzcwNTU4OTM1fDA&ixlib=rb-4.1.0&q=60&w=800"
                    alt="Colorful roller skates"
                    className="relative rounded-3xl shadow-2xl object-cover w-full h-[500px]"
                  />
                  {/* Floating card */}
                  <div className="absolute -bottom-6 -left-6 glass rounded-2xl p-4 border border-primary/30 animate-float">
                    <div className="flex items-center gap-3">
                      <div className="w-12 h-12 rounded-full bg-primary/20 flex items-center justify-center">
                        <MapPin className="w-6 h-6 text-primary" />
                      </div>
                      <div>
                        <p className="text-sm text-muted-foreground">Next event</p>
                        <p className="font-semibold">London Roller Disco</p>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Tagline Section */}
      <section className="py-2 px-4 md:px-8 -mt-2 md:-mt-20">
        <div className="max-w-7xl mx-auto">
          <h2 className="font-heading text-4xl md:text-5xl lg:text-6xl uppercase tracking-wide">
            <span className="text-foreground">EACH ONE</span>{' '}
            <span className="gradient-text">HELP ONE ...</span>
          </h2>
        </div>
      </section>

      {/* Popular Routes Section */}
      <section className="py-4 px-4 md:px-8 mt-2 md:mt-12">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-2 gap-8 items-start">
            {/* Left column: London Bridge image + Trust section below it */}
            <div>
              <div className="relative mb-6">
                <div className="absolute inset-0 bg-gradient-to-br from-primary/20 to-secondary/20 rounded-2xl blur-2xl" />
                <img loading="lazy"
                  src="https://static.prod-images.emergentagent.com/jobs/7de881ea-2561-4193-b1ae-270ae3607db6/images/d060d97c93588542825b6579737fd7e6d6f7a078ab724e5a04ae439fc2c67d03.png"
                  alt="Tower Bridge London at night"
                  className="relative rounded-2xl w-full h-64 object-cover"
                />
              </div>

              {/* Trust & Safety — directly under London Bridge image */}
              <div className="space-y-4 md:mt-14" data-testid="trust-section">
                <h2 className="font-heading text-[1.75rem] sm:text-4xl md:text-5xl uppercase">
                  Trust Who You <span className="gradient-text">Travel With</span>
                </h2>
                <p className="text-base text-muted-foreground">
                  We take the time to get to know each of our members. We check reviews, 
                  profiles and IDs, so you know who you're travelling with and can book 
                  your ride with confidence.
                </p>
                
                <div className="grid grid-cols-2 gap-2">
                  {trustFeatures.map((feature, index) => (
                    <div key={index} className="flex items-center gap-2 p-2.5 rounded-xl bg-background/50 border border-border/50">
                      <div className="w-8 h-8 rounded-full bg-primary/20 flex items-center justify-center text-primary flex-shrink-0">
                        {feature.icon}
                      </div>
                      <span className="font-medium text-xs">{feature.label}</span>
                    </div>
                  ))}
                  <div className="flex items-center gap-2 p-2.5 rounded-xl bg-background/50 border border-border/50">
                    <div className="w-8 h-8 rounded-full bg-primary/20 flex items-center justify-center text-primary flex-shrink-0">
                      <Mail className="w-4 h-4" />
                    </div>
                    <span className="font-medium text-xs">Email Verified</span>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-2">
                  <div 
                    onClick={() => navigate('/safety')}
                    className="flex items-center gap-2 p-2.5 rounded-xl bg-background/50 border border-primary/50 hover:bg-primary/10 transition-colors cursor-pointer group"
                  >
                    <div className="w-8 h-8 rounded-full bg-primary/20 flex items-center justify-center text-primary flex-shrink-0">
                      <Shield className="w-4 h-4" />
                    </div>
                    <span className="font-medium text-xs text-primary underline underline-offset-2 group-hover:text-primary/80">Read Safety Tips</span>
                  </div>
                  <a 
                    href="https://instagram.com/rollm8s"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-2 p-2.5 rounded-xl bg-background/50 border border-primary/50 hover:bg-primary/10 transition-colors group"
                  >
                    <div className="w-8 h-8 rounded-full bg-primary/20 flex items-center justify-center text-primary flex-shrink-0">
                      <Instagram className="w-4 h-4" />
                    </div>
                    <span className="font-medium text-xs text-primary underline underline-offset-2 group-hover:text-primary/80">@RollM8s</span>
                  </a>
                </div>
              </div>
            </div>

            {/* Right column: Routes List */}
            <div>
              <h2 className="font-heading text-3xl md:text-5xl lg:text-6xl uppercase mb-2">
                Popular <span className="gradient-text">Routes</span>
              </h2>
              <p className="text-muted-foreground mb-6">
                Our community travels these routes regularly to events across the UK
              </p>
              
              <div className="space-y-2 md:space-y-4">
                {popularRoutes.map((route) => {
                  // Determine pricing based on route
                  const isShortRoute = route.to_city === 'Harrow' || route.to_city === 'Brixton';
                  const routePricing = {
                    'Essex': { single: '£3-£8', return: '£8.50' },
                    'Harrow': { single: '£3', return: '£5' },
                    'Reading': { single: '£3-£6.40', return: '£8.50' },
                    'Brixton': { single: '£3', return: '£5' },
                    'Bedford': { single: '£3-£8', return: '£8.50' },
                    'Southend': { single: '£3-£6.40', return: '£8.50' }
                  };
                  const pricing = routePricing[route.to_city] || { single: '£3-£8', return: '£8.50' };
                  
                  return (
                    <div 
                      key={route.id}
                      className="flex items-center p-3 md:p-4 rounded-lg bg-card/50 border border-border/30"
                    >
                      <span className="font-medium flex-shrink-0">{route.from_city}</span>
                      <div className="flex-1 mx-3 flex items-center">
                        <svg className="w-4 h-4 text-primary flex-shrink-0 -mr-0.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="15 18 9 12 15 6" /></svg>
                        <div className="h-[2px] flex-1 bg-gradient-to-r from-primary to-secondary" />
                        <svg className="w-4 h-4 text-secondary flex-shrink-0 -ml-0.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="9 18 15 12 9 6" /></svg>
                      </div>
                      <span className="font-medium flex-shrink-0">{route.to_city}</span>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-10 px-4 md:px-8 border-y border-border/50">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {stats.map((stat, index) => (
              <div key={index} className="text-center">
                <p className="font-heading text-4xl md:text-5xl gradient-text">{stat.value}</p>
                <p className="text-muted-foreground mt-2">{stat.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-12 px-4 md:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-10">
            <h2 className="font-heading text-4xl md:text-5xl uppercase mb-4">
              Why <span className="gradient-text">RollM8s</span>?
            </h2>
            <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
              We're not another ride-share app. We're a full community platform built specifically for the UK Skate, Wheels & Boards scene — from event tickets to offline crisis maps.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {features.map((feature, index) => (
              <Card 
                key={index} 
                className="bg-card border-border/50 hover:border-primary/50 transition-all duration-300 card-hover"
                data-testid={`feature-card-${index}`}
              >
                <CardContent className="p-6 space-y-4">
                  <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-primary/10 to-secondary/10 flex items-center justify-center">
                    {feature.icon}
                  </div>
                  <h3 className="font-semibold text-xl">{feature.title}</h3>
                  <p className="text-muted-foreground">{feature.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* How it Works Section */}
      <section className="py-12 px-4 md:px-8 bg-card/50">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-10">
            <h2 className="font-heading text-4xl md:text-5xl uppercase mb-4">
              How It <span className="gradient-text">Works</span>
            </h2>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {[
              { step: "01", title: "Find an Event", desc: "Browse UK events, buy tickets, or discover global skate trips on the World Trips calendar" },
              { step: "02", title: "Pick a Ride or Explore", desc: "Book a ride-share, explore 72K+ crisis resources on the offline map, or browse Community Listings" },
              { step: "03", title: "Roll & Connect", desc: "Navigate with real-time sat-nav, chat with the community, and build your skater corner" }
            ].map((item, index) => (
              <div key={index} className="relative">
                <span className="font-heading text-8xl text-primary/10 absolute -top-6 -left-2">{item.step}</span>
                <div className="relative pt-8">
                  <h3 className="font-heading text-2xl mb-3">{item.title}</h3>
                  <p className="text-muted-foreground">{item.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-12 px-4 md:px-8">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="font-heading text-4xl md:text-5xl uppercase mb-6">
            Ready to <span className="gradient-text">Roll</span>?
          </h2>
          <p className="text-xl text-muted-foreground mb-8">
            Join the UK Skate, Wheels & Boards community. Events, rides, gear, maps, and worldwide trips — all here.
          </p>
          <Button 
            onClick={() => navigate('/register')} 
            className="rounded-full px-12 py-6 text-lg font-bold bg-primary hover:bg-primary/90 neon-glow"
            data-testid="final-cta-btn"
          >
            Register / Login <span className="inline-block animate-bounce-x ml-1">&rarr;</span>
          </Button>
          <p className="text-sm text-muted-foreground mt-4">
            No subscription. Pay only when you ride.
          </p>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-16 px-4 md:px-8" id="faq" data-testid="faq-section">
        <div className="max-w-3xl mx-auto">
          <h2 className="text-2xl sm:text-3xl font-black tracking-tight text-center mb-2">
            Frequently Asked <span className="gradient-text">Questions</span>
          </h2>
          <p className="text-sm text-muted-foreground text-center mb-8">Everything you need to know about RollM8s</p>
          <Accordion type="single" collapsible className="space-y-2">
            <AccordionItem value="what-is" className="border border-border/50 rounded-xl px-4 overflow-hidden">
              <AccordionTrigger className="text-sm font-semibold hover:no-underline py-4" data-testid="faq-what-is">What is RollM8s?</AccordionTrigger>
              <AccordionContent className="text-sm text-muted-foreground pb-4">
                RollM8s is the UK Skate, Wheels & Boards community platform — built first for roller skaters and now open to inline skaters, skateboarders, longboarders, BMX riders, scooter riders and snow sports crews. It brings together events, ride-sharing, a marketplace, offline crisis mapping, world trips, and real community features — all in one app, built by skaters for the whole community.
              </AccordionContent>
            </AccordionItem>
            <AccordionItem value="free" className="border border-border/50 rounded-xl px-4 overflow-hidden">
              <AccordionTrigger className="text-sm font-semibold hover:no-underline py-4" data-testid="faq-free">Is it free to use?</AccordionTrigger>
              <AccordionContent className="text-sm text-muted-foreground pb-4">
                Registering, browsing events, using the ride-share, Community Listings, and community features are all free. You only pay when purchasing event tickets. Some premium features like the Blackout Zone offline sat-nav may require a subscription in the future.
              </AccordionContent>
            </AccordionItem>
            <AccordionItem value="tickets" className="border border-border/50 rounded-xl px-4 overflow-hidden">
              <AccordionTrigger className="text-sm font-semibold hover:no-underline py-4" data-testid="faq-tickets">How do I buy event tickets?</AccordionTrigger>
              <AccordionContent className="text-sm text-muted-foreground pb-4">
                Browse events, pick your ticket tier (Early Bird, Standard, VIP), and pay securely via Stripe. Your tickets appear in your Ticket Wallet with QR codes for scanning at the door. If an event is sold out, you can join the waitlist and get notified when spots open.
              </AccordionContent>
            </AccordionItem>
            <AccordionItem value="rideshare" className="border border-border/50 rounded-xl px-4 overflow-hidden">
              <AccordionTrigger className="text-sm font-semibold hover:no-underline py-4" data-testid="faq-rideshare">How does the ride-share work?</AccordionTrigger>
              <AccordionContent className="text-sm text-muted-foreground pb-4">
                This is not a taxi service — it's a cost-sharing model for community members heading to the same events. Drivers offer seats and set pickup points. Passengers share the petrol cost fairly based on distance. Nobody profits. Drivers get full turn-by-turn sat-nav and passengers can track the driver's live location.
              </AccordionContent>
            </AccordionItem>
            <AccordionItem value="crisis" className="border border-border/50 rounded-xl px-4 overflow-hidden">
              <AccordionTrigger className="text-sm font-semibold hover:no-underline py-4" data-testid="faq-crisis">What is the Blackout Zone?</AccordionTrigger>
              <AccordionContent className="text-sm text-muted-foreground pb-4">
                The Blackout Zone maps over 72,000 UK emergency resources — food banks, water points, shelters, medical facilities, charging stations, and more. It's designed for blackouts, natural disasters, or any situation where you might lose internet or mobile signal.
              </AccordionContent>
            </AccordionItem>
            <AccordionItem value="offline" className="border border-border/50 rounded-xl px-4 overflow-hidden">
              <AccordionTrigger className="text-sm font-semibold hover:no-underline py-4" data-testid="faq-offline">How does offline navigation work?</AccordionTrigger>
              <AccordionContent className="text-sm text-muted-foreground pb-4">
                Download the full UK road network once while you have signal. After that, the app can calculate routes and give you turn-by-turn voice directions entirely in your browser — no internet, no mobile signal, no server needed. It uses a built-in routing engine that runs on your device.
              </AccordionContent>
            </AccordionItem>
            <AccordionItem value="world-trips" className="border border-border/50 rounded-xl px-4 overflow-hidden">
              <AccordionTrigger className="text-sm font-semibold hover:no-underline py-4" data-testid="faq-world-trips">What is World Trips?</AccordionTrigger>
              <AccordionContent className="text-sm text-muted-foreground pb-4">
                World Trips is a global calendar of international roller skating events. Discover events from 14+ countries, see who's going, mark yourself as attending, and connect with skaters planning the same trips. Each event includes city images, dates, and full descriptions.
              </AccordionContent>
            </AccordionItem>
            <AccordionItem value="sk8-shop" className="border border-border/50 rounded-xl px-4 overflow-hidden">
              <AccordionTrigger className="text-sm font-semibold hover:no-underline py-4" data-testid="faq-sk8shop">How do I post on Community Listings?</AccordionTrigger>
              <AccordionContent className="text-sm text-muted-foreground pb-4">
                Register an account, head to Community Listings, and post your item with photos, a description, and your price. You can sell gear (skates, boards, BMX, scooter parts, helmets, pads, snow gear), offer services like coaching or custom paint jobs, give items away for free, or swap with other community members. Buyers can message you directly through the app.
              </AccordionContent>
            </AccordionItem>
            <AccordionItem value="privacy" className="border border-border/50 rounded-xl px-4 overflow-hidden">
              <AccordionTrigger className="text-sm font-semibold hover:no-underline py-4" data-testid="faq-privacy">Is my data safe?</AccordionTrigger>
              <AccordionContent className="text-sm text-muted-foreground pb-4">
                Yes. We take privacy seriously. Payments are processed securely through Stripe — we never see your card details. Location data used for navigation stays on your device. Check our Privacy Policy for full details on how we handle your information.
              </AccordionContent>
            </AccordionItem>
            <AccordionItem value="contact" className="border border-border/50 rounded-xl px-4 overflow-hidden">
              <AccordionTrigger className="text-sm font-semibold hover:no-underline py-4" data-testid="faq-contact">How do I contact support?</AccordionTrigger>
              <AccordionContent className="text-sm text-muted-foreground pb-4">
                You can reach us through the Support page in the app, or email us directly at connect@rollm8s.com. We're also active on Instagram @RollM8s.
              </AccordionContent>
            </AccordionItem>
          </Accordion>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 px-4 md:px-8 border-t border-border/50">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col md:flex-row justify-between items-center gap-6">
            <div className="flex items-center gap-1">
              <img loading="lazy"
                src={mode === 'dark' ? '/rollm8s-logo-dark.png' : '/rollm8s-logo-light.png'} 
                alt="RollM8s Logo" 
                className="w-[6rem] h-[6rem] md:w-[10rem] md:h-[10rem] object-contain flex-shrink-0"
              />
              <div className="relative pb-3">
                <span className="font-logo text-[2.6rem] md:text-[3.5rem] tracking-wide leading-tight">
                  <span className="text-foreground">ROLL</span>
                  <span className="gradient-text">M8S</span>
                </span>
                <span className="absolute left-0 right-0 bottom-0 text-[8px] md:text-[10px] font-bold uppercase leading-none whitespace-nowrap" style={{ textAlign: 'justify', textAlignLast: 'justify' }}>
                  <span className="text-foreground">FOR SKATE, WHE</span>
                  <span className="text-primary">ELS </span>
                  <span className="text-secondary">& BOARDS</span>
                </span>
              </div>
            </div>
            
            <div className="flex flex-wrap justify-center items-center gap-x-4 gap-y-2 md:gap-6 text-sm text-muted-foreground">
              <Link to="/events" className="hover:text-foreground transition-colors">Events</Link>
              <Link to="/fuel-estimator" className="hover:text-foreground transition-colors">Fuel Calculator</Link>
              <Link to="/safety" className="hover:text-foreground transition-colors">Safety Tips</Link>
              <a href="#faq" className="hover:text-foreground transition-colors">FAQ</a>
              <Link to="/terms" className="hover:text-foreground transition-colors">Terms of Service</Link>
            </div>
            <div className="flex flex-wrap justify-center items-center gap-x-4 gap-y-2 md:gap-6 text-sm text-muted-foreground mt-2">
              <Link to="/privacy" className="hover:text-foreground transition-colors">Privacy Policy</Link>
              <Link to="/refund-policy" className="hover:text-foreground transition-colors">Refund Policy</Link>
              <Link to="/contact" className="hover:text-foreground transition-colors">Contact</Link>
            </div>
            <div className="flex justify-center mt-2">
              <a href="https://instagram.com/RollM8s" target="_blank" rel="noopener noreferrer" className="text-sm hover:text-pink-400 transition-colors flex items-center gap-1 text-muted-foreground" data-testid="instagram-link">
                <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor"><path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z"/></svg>
                @RollM8s
              </a>
            </div>

            <p className="text-sm text-muted-foreground">
              © 2026 RollM8s. UK Only.
            </p>
          </div>
          
          <div className="mt-8 text-center text-xs text-muted-foreground">
            <p>
              RollM8s is the UK Skate, Wheels & Boards community platform. Shared rides, event tickets, offline crisis maps, Community Listings noticeboard,
              World Trips, and more. Drivers are not providing taxi or private hire services.
              All ride payments are petrol cost contributions only.
            </p>
          </div>
        </div>
      </footer>
      <SmartInstallModal
        open={showInstallModal}
        onClose={() => setShowInstallModal(false)}
        deferredPrompt={deferredPrompt}
        onPromptUsed={() => setDeferredPrompt(null)}
      />
      {showShowcase && (
        <ShowcasePage onClose={() => { setShowShowcase(false); sessionStorage.setItem('showcase-shown', 'true'); }} />
      )}
    </div>
  );
};

export default LandingPage;
